import { UiButtonDirective, UiControlDirective } from '@template-ui/directives';
import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  Output,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { UiPanelComponent } from '@template-ui/components';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  WorkflowCapabilityReference,
  WorkflowDefinitionDocument,
} from './platform-workflow-definition-lifecycle.models';
import { PlatformWorkflowDefinitionLifecycleService } from './platform-workflow-definition-lifecycle.service';
import {
  addWorkflowCapability,
  capabilityReferencesShapeError,
  supportsWorkflowCapabilities,
  capabilityIdentity,
  installedReference,
  removeWorkflowCapability,
  sameCapabilityReference,
  WorkflowCapabilityCatalogEntry,
} from './workflow-capability-authoring';
import { WorkflowOwnerViewState } from './workflow-owner-view-state';

interface DependencyUse {
  label: string;
  exact: boolean | null;
}

@Component({
  selector: 'phms-workflow-capability-picker',
  imports: [UiButtonDirective, UiControlDirective, UiPanelComponent, CommonModule, FormsModule],
  templateUrl: './workflow-capability-picker.component.html',
  styleUrl: './workflow-capability-picker.component.scss',
})
export class WorkflowCapabilityPickerComponent implements OnInit, OnChanges {
  @Input() document: WorkflowDefinitionDocument | null = null;
  @Input() disabled = false;
  @Input() latestDocument: (() => WorkflowDefinitionDocument | null) | null = null;
  @Input() ownerContext: (() => unknown) | null = null;
  @Output() readonly documentChange =
    new EventEmitter<WorkflowDefinitionDocument>();
  private readonly lifecycle = inject(
    PlatformWorkflowDefinitionLifecycleService,
  );
  private readonly destroyRef = inject(DestroyRef);
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly ownerViews = inject(WorkflowOwnerViewState, { optional: true });
  readonly entries = signal<WorkflowCapabilityCatalogEntry[]>([]);
  readonly loading = signal(false);
  readonly loadError = signal<string | null>(null);
  readonly error = signal<string | null>(null);
  readonly catalogChecked = signal(false);
  dependencySearch = '';
  search = '';
  showUnavailable = false;
  private loadRevision = 0;
  private loadedCatalogKey = '';
  private echoedOwnerKey = '';
  private focusRevision = 0;
  private catalogKey(document: WorkflowDefinitionDocument | null): string {
    return JSON.stringify([document?.workflowCode, document?.packReference]);
  }
  private documentContext(document: WorkflowDefinitionDocument | null): string {
    return JSON.stringify([document?.workflowCode, document?.definitionVersionCode, document?.packReference]);
  }
  private ownerKey(): string { return JSON.stringify([this.ownerContext?.()]); }
  ngOnChanges(changes: SimpleChanges): void {
    const change = changes['document'];
    const owner = this.ownerKey(), ownerChanged = !!this.echoedOwnerKey && owner !== this.echoedOwnerKey;
    const ownerEchoed = !!change || !!changes['ownerContext'] || (this.latestDocument && this.latestDocument() === this.document);
    if (ownerEchoed) this.echoedOwnerKey = owner;
    if ((change && !change.firstChange && this.catalogKey(change.previousValue) !== this.catalogKey(change.currentValue)) ||
      (ownerChanged && ownerEchoed)) this.load();
  }
  get readOnlyReason(): string {
    if (this.document && !this.canUpgrade && !supportsWorkflowCapabilities(this.document.payloadSchemaVersion))
      return 'This saved workflow format is unsupported. Its original dependencies are retained read-only.';
    return capabilityReferencesShapeError(this.document?.capabilityReferences) ?? '';
  }
  get referenceEditingDisabled(): boolean { return this.disabled || !!this.readOnlyReason || !supportsWorkflowCapabilities(this.document?.payloadSchemaVersion); }
  get filteredReferences(): WorkflowCapabilityReference[] {
    const text = this.dependencySearch.trim().toLowerCase();
    return this.orderedReferences.filter(reference => `${this.referenceName(reference)} ${reference.key.provider} ${reference.key.kind} ${reference.key.code} ${reference.key.version} ${this.referenceModule(reference)} ${this.referenceStatus(reference)} ${this.referenceUsages(reference).map(use => use.label).join(' ')}`.toLowerCase().includes(text));
  }
  get needsReviewCommands(): boolean {
    return !!this.document?.nodes.some(
      (n) => n.routing?.conditionCode === 'review.result',
    );
  }
  get canUpgrade(): boolean {
    return ['1.0', '1.1', '1.2', '1.3'].includes(
      this.document?.payloadSchemaVersion ?? '',
    );
  }
  get filteredEntries(): WorkflowCapabilityCatalogEntry[] {
    const text = this.search.trim().toLowerCase();
    return this.entries().filter(
      (e) =>
        (this.showUnavailable || e.lifecycle === 'available') &&
        `${e.key.code} ${e.module} ${e.key.kind} ${this.capabilityName(e.key)} ${this.kindLabel(e.key.kind)}`
          .toLowerCase()
          .includes(text),
    );
  }
  get orderedReferences(): WorkflowCapabilityReference[] {
    if (capabilityReferencesShapeError(this.document?.capabilityReferences)) return [];
    return [...(this.document?.capabilityReferences ?? [])].sort(
      (a, b) =>
        Number(!this.referenceUsages(a).length) -
        Number(!this.referenceUsages(b).length),
    );
  }
  get missingReferences(): WorkflowCapabilityReference[] {
    if (this.readOnlyReason) return [];
    const saved = this.document?.capabilityReferences ?? [];
    const missing = new Map<string, WorkflowCapabilityReference>();
    for (const use of this.declaredReferences()) {
      if (
        !saved.some(
          (reference) =>
            capabilityIdentity(reference.key) ===
            capabilityIdentity(use.reference.key),
        )
      )
        missing.set(JSON.stringify(use.reference), use.reference);
    }
    return [...missing.values()];
  }
  referenceUsages(reference: WorkflowCapabilityReference): DependencyUse[] {
    const id = capabilityIdentity(reference.key);
    const uses: DependencyUse[] = this.declaredReferences()
      .filter((use) => capabilityIdentity(use.reference.key) === id)
      .map((use) => ({
        label: use.label,
        exact: sameCapabilityReference(use.reference, reference),
      }));
    for (const other of this.document?.capabilityReferences ?? []) {
      if (capabilityIdentity(other.key) === id) continue;
      const entries = this.entries().filter(
        (entry) =>
          capabilityIdentity(entry.key) === capabilityIdentity(other.key),
      );
      const dependencies = entries
        .flatMap((entry) => entry.dependencies)
        .filter((dependency) => capabilityIdentity(dependency.key) === id);
      if (entries.length > 1 && dependencies.length) {
        uses.push({
          label: `Installed dependency metadata is ambiguous for “${this.referenceName(other)}”; at least one installed entry requires this dependency.`,
          exact: null,
        });
        continue;
      }
      for (const dependency of dependencies)
        uses.push({
          label: `Required by dependency “${this.referenceName(other)}”`,
          exact: sameCapabilityReference(dependency, reference),
        });
    }
    return uses;
  }
  private declaredReferences(): Array<{
    reference: WorkflowCapabilityReference;
    label: string;
  }> {
    const uses: Array<{
      reference: WorkflowCapabilityReference;
      label: string;
    }> = [];
    const add = (
      reference: WorkflowCapabilityReference | null | undefined,
      label: string,
    ) => {
      if (reference?.key) uses.push({ reference, label });
    };
    const nodes = this.document?.nodes ?? [];
    const counts = new Map<string, number>();
    for (const node of nodes)
      if (node) counts.set(node.id, (counts.get(node.id) ?? 0) + 1);
    for (const node of nodes) {
      if (!node) continue;
      const name =
        counts.get(node.id) === 1
          ? node.displayName || node.stepCode || node.id
          : `${node.id} (ambiguous step)`;
      const declaration = node.declaration;
      for (const command of declaration?.behavior?.commands ?? [])
        add(command.capability, `Used by step “${name}” · staff action`);
      add(
        declaration?.detail?.mainInteraction?.command,
        `Used by step “${name}” · Staff page form`,
      );
      for (const dashboard of declaration?.dashboards ?? []) {
        const dashboardName = dashboard.label || dashboard.id;
        add(
          dashboard.view,
          `Used by dashboard “${dashboardName}” in step “${name}”`,
        );
        for (const action of dashboard.quickActions ?? [])
          add(
            action,
            `Used by dashboard “${dashboardName}” in step “${name}” · quick action`,
          );
      }
    }
    for (const trigger of this.document?.triggers ?? [])
      for (const action of trigger?.actions ?? [])
        add(action.command, `Used by triggered action “${action.id}” · ${nodes.find(node => node?.id === trigger.nodeId)?.displayName || trigger.nodeId} · ${this.eventLabel(trigger.kind)}`);
    return uses;
  }
  private eventLabel(kind: string): string {
    return ({ entry: 'When work arrives', exit: 'When work leaves', transition: 'Along a selected path', return: 'Along a return path',
      decision: 'When a decision is recorded', 'assignment-change': 'When assignment changes' } as Record<string, string>)[kind] ?? `Saved event · ${kind}`;
  }
  capabilityName(key: WorkflowCapabilityReference['key']): string {
    const labels: Record<string, string> = {
      'workflow.step.complete': 'Complete step',
      'workflow.form.save': 'Save form',
      'workflow.form.submit': 'Submit form',
      'workflow.review.request': 'Request review',
      'workflow.review.record': 'Record review',
      'workflow.review.submit': 'Submit review',
      'workflow.preparation.submit': 'Submit work for review',
    };
    return key.provider === 'workflow' &&
      key.kind === 'command' &&
      key.version === 1
      ? (labels[key.code] ?? key.code)
      : key.code;
  }
  referenceName(reference: WorkflowCapabilityReference): string {
    return this.capabilityName(reference.key);
  }
  referenceIdentity(reference: WorkflowCapabilityReference): string { return JSON.stringify(reference); }
  referenceModule(reference: WorkflowCapabilityReference): string {
    const entries = this.entries().filter(
      (entry) =>
        capabilityIdentity(entry.key) === capabilityIdentity(reference.key),
    );
    return entries.length === 1
      ? this.moduleLabel(entries[0].module)
      : 'Saved dependency';
  }
  moduleLabel(value: string): string {
    return value
      .split(/[_.-]/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }
  kindLabel(kind: string): string {
    return (
      (
        {
          command: 'Staff action',
          view: 'Dashboard view',
          condition: 'Routing condition',
          query: 'Data query',
        } as Record<string, string>
      )[kind] ?? kind
    );
  }
  entryCanBeAdded(entry: WorkflowCapabilityCatalogEntry): boolean {
    if (
      this.entries().filter(
        (candidate) =>
          capabilityIdentity(candidate.key) === capabilityIdentity(entry.key),
      ).length !== 1
    )
      return false;
    try {
      installedReference(entry);
      return true;
    } catch {
      return false;
    }
  }
  entryAvailability(entry: WorkflowCapabilityCatalogEntry): string {
    if (
      this.entries().filter(
        (candidate) =>
          capabilityIdentity(candidate.key) === capabilityIdentity(entry.key),
      ).length !== 1
    )
      return 'Ambiguous installed metadata; unavailable for new use';
    if (entry.lifecycle !== 'available')
      return (
        entry.unavailableReason || `${entry.lifecycle}; unavailable for new use`
      );
    return this.entryCanBeAdded(entry)
      ? 'Available'
      : 'Installed contract unavailable';
  }
  ngOnInit(): void {
    this.load();
  }
  load(): void {
    if (this.destroyRef.destroyed) return;
    const revision = ++this.loadRevision, key = this.catalogKey(this.document), owner = this.ownerKey();
    this.entries.set([]); this.loadedCatalogKey = ''; this.catalogChecked.set(false);
    this.loading.set(true); this.loadError.set(null);
    const stillCurrent = () => !this.destroyRef.destroyed && revision === this.loadRevision && owner === this.ownerKey() &&
      key === this.catalogKey(this.latestDocument ? this.latestDocument() : this.document);
    this.lifecycle.getCapabilities(this.document ?? undefined).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (entries) => {
        if (!stillCurrent()) return;
        this.loading.set(false);
        if (!Array.isArray(entries) || !entries.every(catalogEntrySupported)) {
          this.loadError.set('The installed module catalog contains unreadable metadata. Saved dependencies are preserved; refresh availability to retry.');
          return;
        }
        this.entries.set(entries); this.loadedCatalogKey = key; this.catalogChecked.set(true);
      },
      error: () => {
        if (!stillCurrent()) return;
        this.entries.set([]); this.loading.set(false);
        this.loadError.set('The installed module catalog could not be loaded. Refresh to try again; saved dependencies are preserved.');
      },
    });
  }
  isSelected(entry: WorkflowCapabilityCatalogEntry): boolean {
    if (capabilityReferencesShapeError(this.document?.capabilityReferences)) return false;
    return !!this.document?.capabilityReferences?.some(
      (r) => capabilityIdentity(r.key) === capabilityIdentity(entry.key),
    );
  }
  referenceStatus(reference: WorkflowCapabilityReference): string {
    if (!this.catalogChecked() || this.loading() || this.loadError())
      return 'Saved reference; availability not checked';
    const matches = this.entries().filter(
      (e) => capabilityIdentity(e.key) === capabilityIdentity(reference.key),
    );
    if (!matches.length) return 'Not installed; saved reference retained';
    if (matches.length !== 1)
      return 'Ambiguous installed metadata; saved reference retained';
    const entry = matches[0];
    if (entry.lifecycle !== 'available')
      return `${entry.lifecycle}; unavailable for new publication`;
    try {
      return sameCapabilityReference(reference, installedReference(entry))
        ? 'Exact installed contract'
        : 'Contract changed; review required';
    } catch {
      return 'Contract unavailable; saved reference retained';
    }
  }
  private mutationDocument(requireCatalog = true): WorkflowDefinitionDocument {
    const latest = this.latestDocument ? this.latestDocument() : this.document;
    if (!this.document || !latest || this.destroyRef.destroyed || this.disabled ||
      (this.ownerContext && this.ownerKey() !== this.echoedOwnerKey) || this.documentContext(latest) !== this.documentContext(this.document))
      throw new Error('The draft context changed. Review the current dependencies before changing them.');
    const shapeError = capabilityReferencesShapeError(latest.capabilityReferences);
    if (shapeError) throw new Error(shapeError);
    if (requireCatalog && (this.loading() || this.loadError() || !this.catalogChecked() || this.loadedCatalogKey !== this.catalogKey(latest)))
      throw new Error('Refresh availability for the current workflow and exact pack before changing dependencies.');
    return latest;
  }
  private mutationFailed(error: unknown): void {
    this.error.set(error instanceof Error ? error.message : 'Review the current dependency selection.');
    const request = ++this.focusRevision, catalog = this.loadRevision, owner = this.echoedOwnerKey;
    const context = (document: WorkflowDefinitionDocument | null) => JSON.stringify([this.documentContext(document), document?.payloadSchemaVersion, document?.capabilityReferences]);
    const source = context(this.document);
    setTimeout(() => {
      if (request !== this.focusRevision || catalog !== this.loadRevision || this.destroyRef.destroyed ||
        (this.ownerContext && owner !== this.ownerKey()) || source !== context(this.latestDocument ? this.latestDocument() : this.document) ||
        this.ownerViews?.focusSuspended() || this.element.nativeElement.closest('[hidden],[inert]')) return;
      this.element.nativeElement.querySelector<HTMLElement>('[data-dependency-error]')?.focus();
    });
  }
  upgrade(): void {
    if (!this.document || this.disabled || !this.canUpgrade || this.readOnlyReason) return;
    try {
      const document = this.mutationDocument(false);
      if (!['1.0', '1.1', '1.2', '1.3'].includes(document.payloadSchemaVersion)) throw new Error('The saved format changed. Review the current draft before upgrading it.');
      this.error.set(null);
      this.documentChange.emit({ ...document, payloadSchemaVersion: '1.4', capabilityReferences: document.capabilityReferences ?? [] });
    } catch (error) { this.mutationFailed(error); }
  }
  add(entry: WorkflowCapabilityCatalogEntry): void {
    if (!this.document || this.disabled || this.loading() || this.loadError() || this.readOnlyReason) return;
    try {
      const document = addWorkflowCapability(this.mutationDocument(), entry, this.entries());
      this.error.set(null); this.documentChange.emit(document);
    } catch (error) { this.mutationFailed(error); }
  }
  remove(reference: WorkflowCapabilityReference): void {
    if (!this.document || this.disabled || this.loading() || this.loadError() || this.readOnlyReason) return;
    try {
      const document = removeWorkflowCapability(this.mutationDocument(), reference, this.entries());
      this.error.set(null); this.documentChange.emit(document);
    } catch (error) { this.mutationFailed(error); }
  }
}

/** Validate only metadata needed for inspection; malformed responses cannot be called a missing installation. */
function catalogEntrySupported(value: unknown): value is WorkflowCapabilityCatalogEntry {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const entry = value as Record<string, unknown>, key = entry['key'];
  if (!key || typeof key !== 'object' || Array.isArray(key)) return false;
  const identity = key as Record<string, unknown>;
  if (!['provider', 'kind', 'code'].every(name => typeof identity[name] === 'string') || !Number.isSafeInteger(identity['version'])) return false;
  const hash = (value: unknown) => value === null || (!!value && typeof value === 'object' && !Array.isArray(value) && typeof (value as Record<string, unknown>)['hash'] === 'string');
  return typeof entry['module'] === 'string' && typeof entry['lifecycle'] === 'string' && typeof entry['contractHash'] === 'string' &&
    hash(entry['input']) && hash(entry['output']) && Array.isArray(entry['dependencies']) && !capabilityReferencesShapeError(entry['dependencies']) &&
    Array.isArray(entry['requiredContext']) && entry['requiredContext'].every(context => typeof context === 'string');
}
